import React, { useEffect, useMemo, useRef, useState } from "react";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:4040/api";
const API_URL = `${API_BASE}/rates`;

const ROUTES = ["Any", "Napier - Chatham Islands", "Timaru - Chatham Islands"];

const UNITS = [
  { value: "per_tonne", label: "Per Tonne" },
  { value: "per_item", label: "Per Item" },
  { value: "per_container", label: "Per Container" },
  { value: "per_pallet", label: "Per Pallet" },
  { value: "per_head", label: "Per Head" },
  { value: "per_kg", label: "Per KG" },
  { value: "per_m3", label: "Per M3" },
  { value: "per_lm", label: "Per Linear Metre" },
  { value: "per_booking", label: "Per Booking" },
  { value: "flat", label: "Flat" },
  { value: "percent", label: "Percent (surcharge)" },
];

const emptyRate = {
  name: "",
  type: "freight",
  route: "Any",
  unit: "per_tonne",
  price: "",
  minimumCharge: "",
  active: true,
  notes: "",
};

export default function RateSettings() {
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyRate);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [routeFilter, setRouteFilter] = useState("all");
  const [typeFilter, setTypeFilter] = useState("all");
  const [selectedIds, setSelectedIds] = useState([]);
  const [importRows, setImportRows] = useState(null);
  const [importErrors, setImportErrors] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchRates();
  }, []);

  const fetchRates = async () => {
    try {
      setLoading(true);
      const response = await fetch(API_URL);
      const data = await response.json();
      setRates(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setRates([]);
    } finally {
      setLoading(false);
    }
  };

  const updateField = (key, value) => {
    setForm((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetForm = () => {
    setForm(emptyRate);
    setEditingId(null);
    setIsFormOpen(false);
  };

  const startEdit = (rate) => {
    setEditingId(rate._id);
    setForm({
      name: rate.name || "",
      type: rate.type || "freight",
      route: rate.route || "Any",
      unit: rate.unit || "per_tonne",
      price: rate.price ?? "",
      minimumCharge: rate.minimumCharge ?? "",
      active: rate.active ?? true,
      notes: rate.notes || "",
    });
    setIsFormOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();

    try {
      setSaving(true);

      const basePayload = {
        ...form,
        price: Number(form.price || 0),
        minimumCharge: Number(form.minimumCharge || 0),
        active: Boolean(form.active),
      };

      const response = await fetch(
        editingId ? `${API_URL}/${editingId}` : API_URL,
        {
          method: editingId ? "PUT" : "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(basePayload),
        }
      );
      if (!response.ok) throw new Error((await response.text()) || "Failed to save rate");

      resetForm();
      await fetchRates();
    } catch (error) {
      console.error(error);
      alert(error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = window.confirm("Delete this rate?");
    if (!confirmed) return;

    try {
      const response = await fetch(`${API_URL}/${id}`, {
        method: "DELETE",
      });

      if (!response.ok) {
        throw new Error("Failed to delete rate");
      }

      if (editingId === id) {
        resetForm();
      }

      setSelectedIds((prev) => prev.filter((sid) => sid !== id));
      await fetchRates();
    } catch (error) {
      console.error(error);
      alert(error.message);
    }
  };

  const toggleSelected = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((sid) => sid !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = (ids) => {
    const allSelected = ids.length > 0 && ids.every((id) => selectedIds.includes(id));
    setSelectedIds(allSelected ? [] : ids);
  };

  const handleBulkDelete = async () => {
    if (selectedIds.length === 0) return;
    const confirmed = window.confirm(
      `Delete ${selectedIds.length} selected rate${selectedIds.length > 1 ? "s" : ""}? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      const results = await Promise.all(
        selectedIds.map((id) =>
          fetch(`${API_URL}/${id}`, { method: "DELETE" }).then((res) => ({ id, ok: res.ok }))
        )
      );
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        console.error("Failed to delete some rates:", failed);
        alert(`${failed.length} rate(s) could not be deleted.`);
      }
      if (editingId && selectedIds.includes(editingId)) {
        resetForm();
      }
      setSelectedIds([]);
      await fetchRates();
    } catch (error) {
      console.error(error);
      alert(error.message);
    }
  };

  // Minimal RFC-4180-ish CSV parser: handles quoted fields, escaped quotes ("") and commas inside quotes.
  const parseCsv = (text) => {
    const rows = [];
    let row = [];
    let field = "";
    let inQuotes = false;

    for (let i = 0; i < text.length; i++) {
      const char = text[i];
      const next = text[i + 1];

      if (inQuotes) {
        if (char === '"' && next === '"') {
          field += '"';
          i++;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          field += char;
        }
      } else if (char === '"') {
        inQuotes = true;
      } else if (char === ",") {
        row.push(field);
        field = "";
      } else if (char === "\n" || char === "\r") {
        if (char === "\r" && next === "\n") i++;
        row.push(field);
        rows.push(row);
        row = [];
        field = "";
      } else {
        field += char;
      }
    }
    if (field.length > 0 || row.length > 0) {
      row.push(field);
      rows.push(row);
    }
    return rows.filter((r) => r.length > 1 || (r.length === 1 && r[0] !== ""));
  };

  const validTypes = ["freight", "livestock", "other"];

  const validateImportRow = (raw) => {
    const errors = [];
    const name = (raw.Name || "").trim();
    const type = (raw.Type || "").trim().toLowerCase();
    const route = (raw.Route || "Any").trim();
    const unit = (raw.Unit || "").trim().toLowerCase();
    const priceRaw = (raw.Price || "").toString().replace(/[$,]/g, "").trim();
    const price = Number(priceRaw);
    const minimumChargeRaw = (raw.MinimumCharge || raw["Minimum Charge"] || "").toString().replace(/[$,]/g, "").trim();
    const minimumCharge = minimumChargeRaw === "" ? 0 : Number(minimumChargeRaw);
    const activeRaw = (raw.Active ?? raw.Status ?? "true").toString().trim().toLowerCase();
    const active = activeRaw === "true" || activeRaw === "active" || activeRaw === "yes";
    const notes = (raw.Notes || "").trim();

    if (!name) errors.push("Missing name");
    if (!validTypes.includes(type)) errors.push(`Unknown type "${raw.Type}"`);
    if (!ROUTES.includes(route)) errors.push(`Unknown route "${raw.Route}"`);
    if (!UNITS.some((u) => u.value === unit)) errors.push(`Unknown unit "${raw.Unit}"`);
    if (priceRaw === "" || Number.isNaN(price)) errors.push(`Invalid price "${raw.Price}"`);
    if (minimumChargeRaw !== "" && Number.isNaN(minimumCharge)) errors.push(`Invalid minimum charge "${raw.MinimumCharge}"`);

    return {
      data: { name, type, route, unit, price, minimumCharge, active, notes },
      errors,
    };
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || "");
      const rows = parseCsv(text);
      if (rows.length < 2) {
        alert("That file doesn't look like it has any data rows.");
        return;
      }
      const headers = rows[0].map((h) => h.trim());
      const dataRows = rows.slice(1);

      const parsed = dataRows.map((cells) => {
        const raw = {};
        headers.forEach((h, idx) => { raw[h] = cells[idx] ?? ""; });
        const { data, errors } = validateImportRow(raw);
        return { raw, data, errors };
      });

      setImportRows(parsed);
      setImportErrors(parsed.filter((r) => r.errors.length > 0));
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const cancelImport = () => {
    setImportRows(null);
    setImportErrors([]);
  };

  const confirmImport = async () => {
    if (!importRows) return;
    const validRows = importRows.filter((r) => r.errors.length === 0);
    if (validRows.length === 0) {
      alert("No valid rows to import.");
      return;
    }

    try {
      setImporting(true);
      const results = await Promise.all(
        validRows.map((r) =>
          fetch(API_URL, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(r.data),
          }).then((res) => ({ ok: res.ok, name: r.data.name }))
        )
      );
      const failed = results.filter((r) => !r.ok);
      if (failed.length > 0) {
        alert(`${failed.length} of ${validRows.length} rates failed to import.`);
      } else {
        alert(`Imported ${validRows.length} rate${validRows.length > 1 ? "s" : ""} successfully.`);
      }
      setImportRows(null);
      setImportErrors([]);
      await fetchRates();
    } catch (error) {
      console.error(error);
      alert("Import failed: " + error.message);
    } finally {
      setImporting(false);
    }
  };

  const filteredRates = useMemo(() => {
    return rates.filter((rate) => {
      const routeOk = routeFilter === "all" || (rate.route || "Any") === routeFilter;
      const typeOk = typeFilter === "all" || rate.type === typeFilter;
      return routeOk && typeOk;
    });
  }, [rates, routeFilter, typeFilter]);

  const stats = useMemo(() => {
    const activeRates = rates.filter((rate) => rate.active);
    const livestockRates = rates.filter((rate) => rate.type === "livestock");
    const freightRates = rates.filter((rate) => rate.type === "freight");
    const napierRates = rates.filter((rate) => (rate.route || "").toLowerCase().includes("napier"));
    const timaruRates = rates.filter((rate) => (rate.route || "").toLowerCase().includes("timaru"));

    return {
      total: rates.length,
      active: activeRates.length,
      livestock: livestockRates.length,
      freight: freightRates.length,
      napier: napierRates.length,
      timaru: timaruRates.length,
    };
  }, [rates]);

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="mx-auto max-w-[1600px] px-8 py-8 lg:px-10">
        <div className="mb-8">
          <h1 className="text-3xl font-bold tracking-tight text-slate-950">
            Rate Settings
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-slate-600">
            Configure master freight and livestock pricing rates.
          </p>
        </div>

        <div className="mb-6 grid grid-cols-2 gap-4 md:grid-cols-3 xl:grid-cols-6">
          <MetricCard label="Total Rates" value={stats.total} />
          <MetricCard label="Active Rates" value={stats.active} />
          <MetricCard label="Livestock" value={stats.livestock} />
          <MetricCard label="Freight" value={stats.freight} />
          <MetricCard label="Napier - CI" value={stats.napier} />
          <MetricCard label="Timaru - CI" value={stats.timaru} />
        </div>

        <div className="mb-4 flex justify-end gap-3">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
          >
            Import CSV
          </button>
          {!isFormOpen && (
            <button
              type="button"
              onClick={() => setIsFormOpen(true)}
              className="rounded-xl bg-slate-950 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800"
            >
              + Add Rate
            </button>
          )}
        </div>

        <div className="space-y-6">
          {isFormOpen && (
          <SectionCard
            title={editingId ? "Edit Rate" : "Add New Rate"}
            description={
              editingId
                ? "Editing selected rate. Save changes or cancel to add a new rate."
                : "Create a new master pricing rate."
            }
            action={
              <button
                type="button"
                onClick={resetForm}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                {editingId ? "Cancel Edit" : "Close"}
              </button>
            }
          >
            <form onSubmit={handleSave}>
              <div className="grid grid-cols-1 gap-4 md:grid-cols-4 xl:grid-cols-7">
                <Field label="Name">
                  <input
                    className="input"
                    value={form.name}
                    onChange={(e) => updateField("name", e.target.value)}
                    placeholder="Cattle"
                    required
                  />
                </Field>

                <Field label="Type">
                  <select
                    className="input"
                    value={form.type}
                    onChange={(e) => updateField("type", e.target.value)}
                  >
                    <option value="livestock">Livestock</option>
                    <option value="freight">Freight</option>
                    <option value="other">Other</option>
                    <option value="surcharge">Surcharge</option>
                    <option value="levy">Levy</option>
                  </select>
                </Field>

                <Field label="Route">
                  <select
                    className="input"
                    value={form.route}
                    onChange={(e) => updateField("route", e.target.value)}
                  >
                    {ROUTES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </Field>

                <Field label="Unit">
                  <select
                    className="input"
                    value={form.unit}
                    onChange={(e) => updateField("unit", e.target.value)}
                  >
                    {UNITS.map((u) => (
                      <option key={u.value} value={u.value}>{u.label}</option>
                    ))}
                  </select>
                </Field>

                <Field label={form.unit === "percent" ? "Surcharge %" : "Price"}>
                  <div className="relative">
                    <input
                      type="number"
                      className="input"
                      value={form.price}
                      onChange={(e) => updateField("price", e.target.value)}
                      placeholder={form.unit === "percent" ? "e.g. 15" : "0"}
                    />
                    {form.unit === "percent" && (
                      <span className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-sm font-semibold text-slate-400">%</span>
                    )}
                  </div>
                  {form.unit === "percent" && (
                    <p className="mt-1 text-xs text-slate-400">Applied as a % surcharge on top of the calculated charge, not a dollar price.</p>
                  )}
                </Field>

                <Field label="Min. Charge">
                  <input
                    type="number"
                    className="input"
                    value={form.minimumCharge}
                    onChange={(e) => updateField("minimumCharge", e.target.value)}
                    placeholder="Optional — e.g. 50"
                  />
                </Field>

                <Field label="Status">
                  <select
                    className="input"
                    value={form.active ? "active" : "inactive"}
                    onChange={(e) =>
                      updateField("active", e.target.value === "active")
                    }
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </Field>
              </div>

              <div className="mt-4">
                <Field label="Notes">
                  <textarea
                    rows="4"
                    className="input min-h-[120px] resize-y"
                    value={form.notes}
                    onChange={(e) => updateField("notes", e.target.value)}
                    placeholder="Optional notes"
                  />
                </Field>
              </div>

              <div className="mt-5 flex justify-end gap-3">
                {editingId ? (
                  <button
                    type="button"
                    onClick={resetForm}
                    className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
                  >
                    Cancel
                  </button>
                ) : null}

                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
                >
                  {saving
                    ? "Saving..."
                    : editingId
                    ? "Save Changes"
                    : "Add Rate"}
                </button>
              </div>
            </form>
          </SectionCard>
          )}

          <SectionCard
            title="Master Rate Card"
            description="All configured rates, with edit and delete actions."
            flush
            action={
              <div className="flex items-center gap-2">
                {selectedIds.length > 0 && (
                  <button
                    type="button"
                    onClick={handleBulkDelete}
                    className="rounded-xl border border-red-300 bg-red-50 px-3 py-2 text-sm font-semibold text-red-700 hover:bg-red-100"
                  >
                    Delete Selected ({selectedIds.length})
                  </button>
                )}
                <select
                  value={routeFilter}
                  onChange={(e) => setRouteFilter(e.target.value)}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                >
                  <option value="all">All routes</option>
                  {ROUTES.map((r) => (
                    <option key={r} value={r}>{r}</option>
                  ))}
                </select>
                <select
                  value={typeFilter}
                  onChange={(e) => setTypeFilter(e.target.value)}
                  className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm text-slate-700"
                >
                  <option value="all">All types</option>
                  <option value="livestock">Livestock</option>
                  <option value="freight">Freight</option>
                  <option value="other">Other</option>
                  <option value="surcharge">Surcharge</option>
                  <option value="levy">Levy</option>
                </select>
              </div>
            }
          >
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left">
                    <th className="px-4 py-3 w-10">
                      <input
                        type="checkbox"
                        checked={filteredRates.length > 0 && filteredRates.every((r) => selectedIds.includes(r._id))}
                        onChange={() => toggleSelectAll(filteredRates.map((r) => r._id))}
                        className="h-4 w-4 rounded border-slate-300"
                      />
                    </th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Name</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Type</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Route</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Unit</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Price</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">Min. Charge</th>
                    <th className="px-4 py-3 font-semibold text-slate-700">
                      Status
                    </th>
                    <th className="px-4 py-3 font-semibold text-slate-700 text-right">
                      Actions
                    </th>
                  </tr>
                </thead>

                <tbody>
                  {loading ? (
                    <tr>
                      <td
                        colSpan="9"
                        className="px-4 py-8 text-center text-slate-500"
                      >
                        Loading rates...
                      </td>
                    </tr>
                  ) : filteredRates.length === 0 ? (
                    <tr>
                      <td
                        colSpan="9"
                        className="px-4 py-8 text-center text-slate-500"
                      >
                        No rates match this filter.
                      </td>
                    </tr>
                  ) : (
                    filteredRates.map((rate) => {
                      return (
                        <tr
                          key={rate._id}
                          className={`border-t border-slate-200 hover:bg-slate-50 ${
                            editingId === rate._id ? "bg-sky-50" : ""
                          }`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(rate._id)}
                              onChange={() => toggleSelected(rate._id)}
                              className="h-4 w-4 rounded border-slate-300"
                            />
                          </td>

                          <td className="px-4 py-3 font-medium text-slate-900">
                            {rate.name}
                          </td>

                          <td className="px-4 py-3">{toTitle(rate.type)}</td>

                          <td className="px-4 py-3">
                            <RouteBadge route={rate.route || "Any"} />
                          </td>

                          <td className="px-4 py-3">{formatUnit(rate.unit)}</td>

                          <td className="px-4 py-3 font-semibold">
                            {rate.unit === "percent"
                              ? `${Number(rate.price || 0)}%`
                              : `$${Number(rate.price || 0).toLocaleString()}`}
                          </td>

                          <td className="px-4 py-3 text-slate-500">
                            {Number(rate.minimumCharge || 0) > 0
                              ? `$${Number(rate.minimumCharge).toLocaleString()}`
                              : "—"}
                          </td>

                          <td className="px-4 py-3">
                            <StatusBadge active={rate.active} />
                          </td>

                          <td className="px-4 py-3 text-right">
                            <div className="flex justify-end gap-2">
                              <button
                                type="button"
                                onClick={() => startEdit(rate)}
                                className="rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium hover:bg-slate-100"
                              >
                                Edit
                              </button>

                              <button
                                type="button"
                                onClick={() => handleDelete(rate._id)}
                                className="rounded-lg border border-red-300 px-3 py-1.5 text-xs font-medium text-red-700 hover:bg-red-50"
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </SectionCard>
        </div>
      </div>

      {importRows && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-3xl max-h-[85vh] flex flex-col rounded-3xl bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-slate-950">Import Preview</h3>
            <p className="mt-1 text-sm text-slate-500">
              {importRows.length} row{importRows.length !== 1 ? "s" : ""} found —{" "}
              {importRows.length - importErrors.length} valid, {importErrors.length} with errors.
              {importErrors.length > 0 ? " Rows with errors will be skipped." : ""}
            </p>

            <div className="mt-4 flex-1 overflow-y-auto rounded-2xl border border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 border-b border-slate-200 sticky top-0">
                  <tr className="text-left">
                    <th className="px-3 py-2 font-semibold text-slate-700">Name</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Type</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Route</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Unit</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Price</th>
                    <th className="px-3 py-2 font-semibold text-slate-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {importRows.map((r, idx) => (
                    <tr key={idx} className={`border-t border-slate-200 ${r.errors.length > 0 ? "bg-red-50" : ""}`}>
                      <td className="px-3 py-2">{r.raw.Name}</td>
                      <td className="px-3 py-2">{r.raw.Type}</td>
                      <td className="px-3 py-2">{r.raw.Route}</td>
                      <td className="px-3 py-2">{r.raw.Unit}</td>
                      <td className="px-3 py-2">{r.raw.Price}</td>
                      <td className="px-3 py-2">
                        {r.errors.length > 0 ? (
                          <span className="text-xs font-medium text-red-700">{r.errors.join("; ")}</span>
                        ) : (
                          <span className="text-xs font-medium text-emerald-700">OK</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                onClick={cancelImport}
                disabled={importing}
                className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm transition hover:bg-slate-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={confirmImport}
                disabled={importing || importRows.length - importErrors.length === 0}
                className="rounded-xl bg-slate-950 px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:opacity-50"
              >
                {importing ? "Importing..." : `Import ${importRows.length - importErrors.length} Rate${importRows.length - importErrors.length !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .input {
          width: 100%;
          border-radius: 0.9rem;
          border: 1px solid rgb(203 213 225);
          background: white;
          padding: 0.75rem 0.95rem;
          font-size: 0.95rem;
          color: rgb(15 23 42);
          outline: none;
          transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;
        }

        .input::placeholder {
          color: rgb(148 163 184);
        }

        .input:hover {
          border-color: rgb(148 163 184);
        }

        .input:focus {
          border-color: rgb(15 23 42);
          box-shadow: 0 0 0 4px rgba(15, 23, 42, 0.08);
        }
      `}</style>
    </div>
  );
}

function SectionCard({ title, description, children, action, flush = false }) {
  return (
    <section className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="border-b border-slate-200 px-5 py-4 flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          {description ? (
            <p className="mt-1 text-sm text-slate-500">{description}</p>
          ) : null}
        </div>
        {action ? <div>{action}</div> : null}
      </div>
      <div className={flush ? "" : "p-5"}>{children}</div>
    </section>
  );
}

function MetricCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {label}
      </p>
      <p className="mt-2 text-2xl font-bold text-slate-900">{value}</p>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-sm font-medium text-slate-700 mb-2">
        {label}
      </label>
      {children}
    </div>
  );
}

function RouteBadge({ route }) {
  if (route === "Any" || !route) {
    return <span className="text-slate-500">Any</span>;
  }
  const isNapier = route.toLowerCase().includes("napier");
  const isTimaru = route.toLowerCase().includes("timaru");
  const color = isNapier ? "bg-teal-50 text-teal-700" : isTimaru ? "bg-orange-50 text-orange-700" : "bg-slate-100 text-slate-700";
  return (
    <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${color}`}>
      {route}
    </span>
  );
}

function StatusBadge({ active }) {
  return (
    <span
      className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${
        active ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"
      }`}
    >
      {active ? "Active" : "Inactive"}
    </span>
  );
}

function toTitle(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function formatUnit(unit) {
  if (!unit) return "-";
  const match = UNITS.find((u) => u.value === unit);
  return match ? match.label : unit;
}